/**
 * The **epoch slider** — S3's "land-cover time series behind a time slider".
 *
 * A slim bar in the map's bottom-centre slot that steps a series dataset through the years
 * it actually has. Moving it is one store write, `setActiveRaster`, so the map, the legend,
 * the §F5.10 layer count and the Insights panel all follow the same field they already
 * follow — nothing about a year is stored anywhere else, and there is no second source of
 * truth about which raster is drawn.
 *
 * ## Why not the §F5.10 time bar
 *
 * `TimeBar` animates `state.yearIdx` over `/meta/config.years` — the programme reporting
 * window, 2021–2026 — and everything downstream of it asks §D3 for *indicator values* at
 * that year. This slider moves between **catalogue rows**: 2017–2024, one COG each, none of
 * them an indicator and none of them inside the reporting window. Adapting the one control
 * to both would mean a year that sometimes means a reporting period and sometimes a raster,
 * with two different sets of live ticks — which is the ambiguity the client's own
 * "label the epochs honestly" clause is about. So they are two controls; only one of them
 * is on screen at a time, because a theme maps a dataset or a variable and not both.
 *
 * ## Honest labels
 *
 * The years are `layers[].epochs` — written by the harvester from what is on the volume, not
 * from what the product claims — so the slider cannot offer a year the platform cannot draw.
 * The client's specification says 2017–2025; the open stand-in's bucket ends at **2024**, so
 * the slider shows eight ticks and the bar names the product underneath them
 * (`attribution`). Nothing here rounds that up to nine.
 *
 * ## Switched off for land cover (client directive, 9 Sep 2026)
 *
 * The client asked for Land Use to show the 2024 epoch alone, with no slider. The switch is
 * `/meta/config.theme_defaults.locked_series` and is read below — server-side, reversible in
 * one tuple, and nothing about the published catalogue changes. See `seriesLocked`.
 *
 * ## Behind the Animate tool (client, 2026-09-15)
 *
 * The same directive that put the §F5.10 time bar behind a switch applies here: the everyday
 * control is `#epochSelect`, next to the active raster row in the Layers pane
 * (`LayersPane.tsx`'s `ActiveLayer`), which calls the same `setActiveRaster` this bar does.
 * This bar draws only once `state.animateOpen` is set — the map tool column's `#btnAnimate` —
 * in addition to the series conditions above, and closes itself with `#epochbarClose`.
 */
import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../lib/icons';
import { fetchConfig, fetchLayers, queryKeys } from '../../lib/api';
import { useDraggable } from '../../lib/draggable';
import { selectActiveRaster, useAppStore } from '../../lib/state';
import { seriesLocked } from '../sidebar/themes';
import { epochIndexOf, epochLabel, epochsOf, seriesFor } from '../map/epochs';
import { isRasterKind } from '../map/layerKinds';

export function EpochSlider() {
  const { t } = useTranslation();
  const role = useAppStore((s) => s.role);
  const drawn = useAppStore(selectActiveRaster);
  const setActiveRaster = useAppStore((s) => s.setActiveRaster);
  const animateOpen = useAppStore((s) => s.animateOpen);
  const setAnimateOpen = useAppStore((s) => s.setAnimateOpen);
  const barRef = useRef<HTMLDivElement | null>(null);

  useDraggable(barRef, barRef);

  const { data: layers } = useQuery({ queryKey: queryKeys.layers(role), queryFn: fetchLayers });
  const { data: config } = useQuery({ queryKey: queryKeys.config(), queryFn: fetchConfig });
  const series = seriesFor(layers, drawn);
  const epochs = epochsOf(series);
  const idx = series ? epochIndexOf(series, drawn) : -1;

  // Nothing to step: the drawn raster is not a series and is not a year of one.
  if (!series || epochs.length < 2 || idx < 0) return null;
  // Client, 2026-09-15: the bar is behind the Animate map tool — `#epochSelect` (next to the
  // active raster row in the Layers pane) is the everyday control now, and this bar only
  // draws once the reader has explicitly asked for the animation view.
  if (!animateOpen) return null;
  /**
   * **The series' slider is switched off** — client directive, 9 Sep 2026:
   *
   * > *"hide the 2017–2023 epochs and the time slider for now."*
   *
   * `/meta/config.theme_defaults.locked_series` is the switch and the server owns it, so the
   * years come back by emptying one tuple there rather than by shipping a bundle. Nothing is
   * unpublished: `epochs` is still served (which is what keeps the eight per-year rows out of
   * the datasets list), and §H5's change tool still cross-tabs any pair of them.
   *
   * The bar is returned as `null` rather than disabled: a dead slider over a single year is a
   * control asking to be tried, and the reader is meant to read the map as one dataset.
   */
  if (seriesLocked(config, series.id)) return null;

  const rasterIds = (layers ?? []).filter((l) => isRasterKind(l.kind)).map((l) => l.id);
  const goTo = (next: number) => {
    const epoch = epochs[next];
    if (!epoch) return;
    // The **year's own row**, not a tile URL from the series payload: the year's layer
    // carries its own `tile_template`, and that is the path §I1 gates (`serializers.py`).
    setActiveRaster(epoch.layer, rasterIds);
  };

  const current = epochs[idx];
  return (
    <div id="epochbar" ref={barRef} data-series={series.id}>
      <div className="tcol">
        <input
          type="range"
          id="epochSlider"
          min={0}
          max={epochs.length - 1}
          step={1}
          value={idx}
          aria-label={t('epochs.slider', { name: series.name })}
          aria-valuetext={current ? epochLabel(current) : ''}
          onChange={(e) => goTo(Number(e.target.value))}
        />
        <div className="ticks" id="epochTicks">
          {epochs.map((epoch, i) => (
            <button
              type="button"
              key={epoch.year}
              data-epoch={epoch.year}
              data-layer={epoch.layer}
              className={i === idx ? 'active' : ''}
              aria-pressed={i === idx}
              title={t('epochs.goTo', { year: epochLabel(epoch) })}
              onClick={() => goTo(i)}
            >
              {epochLabel(epoch)}
            </button>
          ))}
        </div>
      </div>

      <div className="yr" id="epochLabel">
        {current ? epochLabel(current) : ''}
      </div>

      {/*
        Which product the years come from — the client's "label the epochs honestly" clause,
        and, for a CC-BY source, a condition of use rather than a courtesy.

        **The product name is in the text, not only in `title`** (client review, 2026-09-08).
        This line used to read "2017–2024 published" and put `attribution` on the tooltip,
        which is a year range and a hover: unreachable on a touch screen, unreachable by
        keyboard, and unread by anyone who was not already looking for it. The clause asks
        the slider to *say* which product the epochs come from, so it says it — `source_org`
        because it is the short form the catalogue already keeps for exactly this ("Impact
        Observatory / Esri"), with the full CC-BY attribution still on `title` and still on
        the map's own attribution control. Nothing is composed here beyond the join.
      */}
      <div className="epoch-src" id="epochSource" title={series.attribution ?? series.name}>
        {t('epochs.source', {
          first: epochs[0]?.year,
          last: epochs[epochs.length - 1]?.year,
          source: series.source_org,
        })}
      </div>

      <button
        type="button"
        className="mapbtn"
        id="epochbarClose"
        style={{ width: 24, height: 24 }}
        aria-label={t('timebar.close')}
        title={t('timebar.close')}
        onClick={() => setAnimateOpen(false)}
      >
        <Icon name="x" size={13} />
      </button>
    </div>
  );
}

export default EpochSlider;
